import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";
import { PasswordStrength, checkPasswordPolicy } from "@/components/PasswordStrength";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const { pass } = checkPasswordPolicy(pw);
    if (!pass) {
      setErr("Please choose a password that meets all the requirements.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      await refresh();
      nav("/dashboard/forecast", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8">
        <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to login
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <RecrystaMark size={36} />
          <div className="text-sm font-bold text-slate-900">Recrysta</div>
        </div>
        <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900">Set a new password</h1>
        <p className="mt-2 text-sm text-slate-500">Choose a password with at least 10 characters that includes upper/lower case, a digit and a symbol.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">New password</label>
            <input
              type="password"
              required
              minLength={10}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-password-new"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
            {pw && <PasswordStrength password={pw} />}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Confirm password</label>
            <input
              type="password"
              required
              minLength={10}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-password-confirm"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
          {err && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="reset-password-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-password-submit"
            className="w-full rounded-lg bg-slate-900 text-white py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
